import React from 'react'
import { X } from 'lucide-react'
import { Input } from '@/components/ui/input'

// Custom Places Autocomplete, ported from tm2-admin. The legacy Google
// `Autocomplete` widget renders its dropdown as `.pac-container` on
// document.body; inside a Radix modal Dialog the body has pointer-events:none,
// so clicks on suggestions never register. Building the dropdown ourselves
// keeps it inside the dialog's DOM tree. Callers only mount this once Google
// Maps is loaded (the `loaded` prop in address/show.js), so window.google is
// available at mount.
class AddressAutocomplete extends React.Component {

  state = {
    inputValue: '',
    predictions: [],
    open: false,
    highlightIndex: -1,
  }

  inputRef = React.createRef()

  componentDidMount() {
    const places = window.google?.maps?.places
    if (places) {
      this.autocompleteService = new places.AutocompleteService()
      // PlacesService needs a container element to attribute results to.
      this.placesService = new places.PlacesService(document.createElement('div'))
      this.sessionToken = new places.AutocompleteSessionToken()
    }
    this.inputRef.current?.focus()
  }

  componentWillUnmount() {
    if (this.blurTimer) clearTimeout(this.blurTimer)
  }

  fetchPredictions(input) {
    if (!input || !this.autocompleteService) {
      this.setState({ predictions: [], open: false, highlightIndex: -1 })
      return
    }
    const request = {
      input,
      types: ['geocode'],
      sessionToken: this.sessionToken,
    }

    this.autocompleteService.getPlacePredictions(request, (predictions, status) => {
      const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK
      if (status !== OK || !predictions) {
        this.setState({ predictions: [], open: false, highlightIndex: -1 })
        return
      }
      this.setState({ predictions, open: true, highlightIndex: -1 })
    })
  }

  onInputChange = (e) => {
    this.setState({ inputValue: e.target.value })
    this.fetchPredictions(e.target.value)
  }

  onKeyDown = (e) => {
    const { predictions, highlightIndex, open } = this.state
    if (!open || predictions.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      this.setState({ highlightIndex: Math.min(highlightIndex + 1, predictions.length - 1) })
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      this.setState({ highlightIndex: Math.max(highlightIndex - 1, 0) })
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault()
      this.onSelect(predictions[highlightIndex])
    } else if (e.key === 'Escape') {
      this.setState({ open: false })
    }
  }

  onSelect = (prediction) => {
    if (!this.placesService) return
    this.placesService.getDetails(
      {
        placeId: prediction.place_id,
        fields: ['address_components', 'formatted_address', 'geometry', 'name'],
        sessionToken: this.sessionToken,
      },
      (place, status) => {
        const OK = window.google?.maps?.places?.PlacesServiceStatus?.OK
        if (status !== OK || !place || !place.geometry) return
        // Rotate session token after each selection (Google billing best practice).
        this.sessionToken = new window.google.maps.places.AutocompleteSessionToken()
        this.setState({
          inputValue: place.formatted_address || prediction.description,
          predictions: [],
          open: false,
          highlightIndex: -1,
        })
        this.props.onPlaceChanged?.(place)
      }
    )
  }

  onFocus = () => {
    if (this.state.predictions.length > 0) this.setState({ open: true })
  }

  onBlur = () => {
    // Defer so mousedown on a suggestion can register before we hide.
    this.blurTimer = setTimeout(() => this.setState({ open: false }), 150)
  }

  onClear = () => {
    this.setState({ inputValue: '', predictions: [], open: false, highlightIndex: -1 })
    this.inputRef.current?.focus()
  }

  render() {
    const { inputValue, predictions, open, highlightIndex } = this.state

    return (
      <div className="relative w-full">
        <Input
          ref={this.inputRef}
          type="text"
          placeholder={this.props.placeholder || 'Search by address'}
          value={inputValue}
          onChange={this.onInputChange}
          onKeyDown={this.onKeyDown}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
          className={inputValue ? 'pr-9' : undefined}
        />
        {inputValue && (
          <button
            type="button"
            aria-label="Clear"
            onMouseDown={(e) => { e.preventDefault(); this.onClear() }}
            className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
        {open && predictions.length > 0 && (
          <ul
            role="listbox"
            className="absolute inset-x-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-2xl border border-border bg-popover py-1 text-sm shadow-md"
          >
            {predictions.map((p, i) => (
              <li
                key={p.place_id}
                role="option"
                aria-selected={highlightIndex === i}
                className={`cursor-pointer px-3 py-1.5 hover:bg-muted ${highlightIndex === i ? 'bg-muted' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); this.onSelect(p) }}
              >
                {p.description}
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }
}

export default AddressAutocomplete
